import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  Clock,
  Download,
  ExternalLink,
  ImageIcon,
  Link2,
  Loader2,
  MinusCircle,
  StopCircle,
  XCircle,
  Video,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"; // eslint-disable-line no-unused-vars

import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth.jsx";
import { RunStatusPill } from "@/pages/DemoRunsListPage";
import NarrationPanel from "@/components/NarrationPanel";
import FinalVideoPanel from "@/components/FinalVideoPanel";
import ShareVideoModal from "@/components/ShareVideoModal";
import InvitationsTable from "@/components/InvitationsTable";
import RunFolderChips from "@/components/RunFolderChips";
import InternalVideoPreviewModal from "@/components/InternalVideoPreviewModal";
import RunCostCard from "@/components/RunCostCard";
import ProvenanceCard, { useReplayStepModal, ReplayStepDialog } from "@/components/ProvenanceCard";
import FfmpegStatusBanner from "@/components/FfmpegStatusBanner";
import { formatRunNumber, runLabel } from "@/lib/runNumber";

const TERMINAL = new Set(["completed", "failed", "cancelled"]);

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
}

function formatDuration(ms) {
  if (ms == null) return "—";
  if (ms < 1000) return `${ms}ms`;
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m ${s % 60}s`;
}

function assetUrl(runId, assetId) {
  return `${api.defaults.baseURL}/demo-runs/${runId}/artifacts/${assetId}`;
}

export default function DemoRunDetailPage() {
  const { run_id } = useParams();
  const queryClient = useQueryClient();
  const { hasPermission, token } = useAuth();
  const canCancel = hasPermission("workflow.write");
  const canShare = hasPermission("workflow.run");
  const [lightbox, setLightbox] = useState(null); // {src, name}
  const [shareOpen, setShareOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const runQuery = useQuery({
    queryKey: ["demo-run", run_id],
    queryFn: async () => (await api.get(`/demo-runs/${run_id}`)).data,
    refetchInterval: (q) =>
      q.state.data && TERMINAL.has(q.state.data.status) ? false : 2000,
  });

  const stepsQuery = useQuery({
    queryKey: ["demo-run-steps", run_id],
    queryFn: async () => (await api.get(`/demo-runs/${run_id}/steps`)).data,
    refetchInterval: (q) => {
      const run = runQuery.data;
      return run && TERMINAL.has(run.status) ? false : 2000;
    },
  });

  const assetsQuery = useQuery({
    queryKey: ["demo-run-assets", run_id],
    queryFn: async () => (await api.get(`/demo-runs/${run_id}/assets`)).data,
    refetchInterval: (q) => {
      const run = runQuery.data;
      return run && TERMINAL.has(run.status) ? false : 3000;
    },
  });

  const cancelMutation = useMutation({
    mutationFn: async () =>
      (await api.post(`/demo-runs/${run_id}/cancel`)).data,
    onSuccess: () => {
      toast.success("Cancellation requested — will stop after current step.");
      queryClient.invalidateQueries({ queryKey: ["demo-run", run_id] });
    },
    onError: (err) => toast.error(formatApiError(err)),
  });

  useEffect(() => {
    // Force a final refetch when run goes terminal so we get the final
    // duration_ms + video_path in one tick.
    if (runQuery.data && TERMINAL.has(runQuery.data.status)) {
      queryClient.invalidateQueries({ queryKey: ["demo-run-steps", run_id] });
      queryClient.invalidateQueries({ queryKey: ["demo-run-assets", run_id] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runQuery.data?.status, run_id, queryClient]);

  // R3 Phase 6A.7.1 — replay modal control hook.  MUST be above the
  // early returns to satisfy rules-of-hooks.
  const replay = useReplayStepModal();

  if (runQuery.isLoading) return <Skeleton className="h-96 w-full rounded-xl" />;
  if (runQuery.isError) {
    return (
      <div
        className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700"
        data-testid="run-detail-error"
      >
        {formatApiError(runQuery.error)}
      </div>
    );
  }

  const run = runQuery.data;
  const steps = stepsQuery.data ?? [];
  const assets = assetsQuery.data ?? [];
  const screenshots = assets
    .filter((a) => a.type === "screenshot")
    .sort((a, b) => a.ordering - b.ordering);
  const video = assets.find((a) => a.type === "video");
  const debugAsset = assets.find((a) => a.type === "debug_page");
  const logAsset = assets.find((a) => a.type === "log");
  const isLive = !TERMINAL.has(run.status);

  return (
    <div className="space-y-6" data-testid="run-detail-page">
      <FfmpegStatusBanner />
      <header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex-1">
          <Link
            to="/runs"
            className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-slate-900"
            data-testid="run-back-link"
          >
            <ArrowLeft size={14} />
            Back to runs
          </Link>
          <div className="mt-1 flex flex-wrap items-center gap-3">
            <span className="font-mono text-lg font-semibold text-indigo-600" data-testid="run-number-label">{runLabel(run.run_number)}</span>
            <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
              {run.workflow_name_snapshot}
            </h1>
            <RunStatusPill status={run.status} />
            <span className="font-mono text-sm text-slate-500" data-testid="run-version">
              v{run.workflow_version}
            </span>
          </div>
          <div className="mt-3 grid grid-cols-2 gap-x-6 gap-y-1 text-xs text-slate-500 sm:flex sm:flex-wrap">
            <span>Started {formatDate(run.started_at)}</span>
            <span>·</span>
            <span>
              {run.finished_at ? `Finished ${formatDate(run.finished_at)}` : "Running…"}
            </span>
            <span>·</span>
            <span data-testid="run-duration">Duration: {formatDuration(run.duration_ms)}</span>
            <span>·</span>
            <span>Triggered by <code className="font-mono">{run.triggered_by}</code></span>
            <span>·</span>
            <span>run_id <code className="font-mono">{run.run_id.slice(0, 8)}…</code></span>
          </div>
          <div className="mt-3">
            <RunFolderChips runId={run.run_id} />
          </div>
        </div>
        {isLive && canCancel && (
          <Button
            variant="outline"
            onClick={() => cancelMutation.mutate()}
            disabled={cancelMutation.isPending}
            className="gap-2 border-rose-200 text-rose-700 hover:bg-rose-50"
            data-testid="run-cancel-button"
          >
            <StopCircle size={14} />
            Cancel run
          </Button>
        )}
        {run.status === "completed" && canShare && (
          <Button
            variant="outline"
            onClick={() => setPreviewOpen(true)}
            className="gap-2 border-indigo-200 text-indigo-700 hover:bg-indigo-50"
            data-testid="run-preview-video-button"
          >
            <Video size={14} />
            Preview Video
          </Button>
        )}
        {run.status === "completed" && canShare && (
          <Button
            variant="outline"
            onClick={() => setShareOpen(true)}
            className="gap-2 border-indigo-200 text-indigo-700 hover:bg-indigo-50"
            data-testid="run-share-button"
          >
            <Link2 size={14} />
            Share with viewers
          </Button>
        )}
      </header>

      {run.status === "failed" && run.error_message && (
        <div
          className="flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800"
          data-testid="run-error-banner"
        >
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <div>
            <div className="font-medium">Run failed</div>
            <div className="mt-0.5 font-mono text-xs">{run.error_message}</div>
          </div>
        </div>
      )}

      {TERMINAL.has(run.status) && <FinalVideoPanel runId={run_id} />}

      {/* R3 Phase 6A.7.1 — Generation provenance.  Self-hides for
          non-AI runs (the endpoint returns 404, which the card
          interprets as a no-op). */}
      <ProvenanceCard runId={run_id} />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <Section
            title={`Steps · ${run.completed_steps} / ${run.total_steps} complete`}
          >
            {steps.length === 0 ? (
              <p className="text-sm text-slate-500" data-testid="run-steps-empty">
                Steps will appear here as the run progresses.
              </p>
            ) : (
              <ol className="space-y-1.5" data-testid="run-steps-list">
                {steps.map((s) => (
                  <StepRow
                    key={s.step_id}
                    step={s}
                    runId={run_id}
                    assets={assets}
                    onReplay={() =>
                      replay.open({
                        runId: run_id,
                        stepId: s.step_id,
                        stepName: s.step_name,
                      })
                    }
                  />
                ))}
              </ol>
            )}
          </Section>

          <Section
            title={
              <span className="flex items-center gap-2">
                <ImageIcon size={16} className="text-indigo-600" />
                Screenshots · {screenshots.length}
              </span>
            }
          >
            {screenshots.length === 0 ? (
              <p className="text-sm text-slate-500">No screenshots yet.</p>
            ) : (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3" data-testid="run-screenshots-grid">
                {screenshots.map((a) => (
                  <button
                    key={a.asset_id}
                    type="button"
                    onClick={() => setLightbox({ src: assetUrl(run_id, a.asset_id), name: a.filename })}
                    className="group overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm hover:border-indigo-300"
                    data-testid={`screenshot-thumb-${a.asset_id}`}
                  >
                    <AuthedImg
                      runId={run_id}
                      assetId={a.asset_id}
                      alt={a.filename}
                      className="aspect-video w-full object-cover transition group-hover:opacity-90"
                    />
                    <div className="border-t border-slate-100 px-2 py-1 text-left text-[10px] font-mono text-slate-500 truncate">
                      {a.filename}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </Section>

          <NarrationPanel runId={run_id} runStatus={run.status} />
        </div>

        <div className="space-y-6">
          <RunCostCard runId={run_id} />
          <Section
            title={
              <span className="flex items-center gap-2">
                <Video size={16} className="text-indigo-600" />
                Session video
              </span>
            }
          >
            {video ? (
              <AuthedVideo runId={run_id} assetId={video.asset_id} />
            ) : isLive ? (
              <p className="flex items-center gap-2 text-sm text-slate-500">
                <Loader2 size={14} className="animate-spin" />
                Video will be available when the run finishes.
              </p>
            ) : (
              <p className="text-sm text-slate-500">No video captured.</p>
            )}
          </Section>

          <Section title={`Variables used · ${Object.keys(run.variables_used).length}`}>
            <dl className="space-y-1 text-xs" data-testid="run-variables-list">
              {Object.entries(run.variables_used).map(([k, v]) => (
                <div key={k} className="flex items-center justify-between gap-3 rounded border border-slate-100 px-2 py-1">
                  <dt className="font-mono text-slate-600">{k}</dt>
                  <dd
                    className={
                      v === "[redacted env var]"
                        ? "font-mono text-slate-400 italic"
                        : "font-mono text-slate-800 truncate max-w-[12rem]"
                    }
                    data-testid={`run-variable-${k}`}
                    title={v}
                  >
                    {v}
                  </dd>
                </div>
              ))}
            </dl>
          </Section>

          {(debugAsset || logAsset) && (
            <Section title="Diagnostics">
              <ul className="space-y-2 text-sm">
                {logAsset && (
                  <li>
                    <AuthedDownload
                      runId={run_id}
                      assetId={logAsset.asset_id}
                      filename={logAsset.filename}
                      label="Download run.log"
                      testId="run-log-link"
                    />
                  </li>
                )}
                {debugAsset && (
                  <li>
                    <AuthedDownload
                      runId={run_id}
                      assetId={debugAsset.asset_id}
                      filename={debugAsset.filename}
                      label="Download debug_page.html"
                      testId="debug-page-link"
                    />
                  </li>
                )}
              </ul>
            </Section>
          )}
        </div>
      </div>

      <Dialog open={!!lightbox} onOpenChange={(o) => !o && setLightbox(null)}>
        <DialogContent className="max-w-5xl">
          <DialogTitle className="text-sm font-mono text-slate-500">
            {lightbox?.name}
          </DialogTitle>
          {lightbox && (
            <AuthedImg
              src={lightbox.src}
              alt={lightbox.name}
              className="max-h-[78vh] w-full object-contain"
            />
          )}
        </DialogContent>
      </Dialog>

      <ShareVideoModal
        runId={run_id}
        open={shareOpen}
        onOpenChange={setShareOpen}
      />

      <InternalVideoPreviewModal
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        runId={run_id}
        runNumberLabel={runLabel(run.run_number)}
        workflowName={run.workflow_name_snapshot}
      />

      {canShare && TERMINAL.has(run.status) && (
        <InvitationsTable runId={run_id} />
      )}

      {/* R3 Phase 6A.7.1 — replay-step dialog.  Driven by the
          useReplayStepModal hook above; renders nothing until a
          StepRow's Replay button opens it. */}
      <ReplayStepDialog control={replay} />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <h2 className="mb-3 text-sm font-semibold tracking-tight text-slate-900">{title}</h2>
      {children}
    </section>
  );
}

function StepRow({ step, runId, assets, onReplay }) {
  const [expanded, setExpanded] = useState(false);
  const stepShot = assets.find(
    (a) => a.type === "screenshot" && a.step_id === step.step_id,
  );
  const Icon =
    step.status === "ok"
      ? CheckCircle2
      : step.status === "failed"
      ? XCircle
      : step.status === "running"
      ? Loader2
      : step.status === "skipped"
      ? MinusCircle
      : Clock;
  const iconCls =
    step.status === "ok"
      ? "text-emerald-600"
      : step.status === "failed"
      ? "text-rose-600"
      : step.status === "running"
      ? "text-indigo-600 animate-spin"
      : "text-slate-400";

  // R3 Phase 6A.7.1 — replay is exposed for every status except
  // `running` (where the step hasn't finished yet).  Failed steps
  // surface the button prominently in rose; others get a subtle
  // slate hover.
  const canReplay = onReplay && step.status && step.status !== "running";

  return (
    <li
      className="rounded-lg border border-slate-200 bg-white"
      data-testid={`run-step-${step.step_order}`}
    >
      <div className="flex w-full items-center gap-3 px-3 py-2 text-left">
        <button
          type="button"
          onClick={() => setExpanded((x) => !x)}
          className="flex flex-1 items-center gap-3"
        >
          <Icon size={16} className={iconCls} />
          <span className="grid h-6 w-6 flex-none place-items-center rounded-md bg-slate-50 font-mono text-[10px] font-semibold text-slate-500">
            {step.step_order}
          </span>
          <span className="flex-1 truncate text-sm font-medium text-slate-800">{step.step_name}</span>
          <span className="font-mono text-[10px] text-slate-400">{step.action}</span>
          <span className="font-mono text-[10px] text-slate-500">
            {step.duration_ms != null ? `${step.duration_ms}ms` : "—"}
          </span>
        </button>
        {canReplay && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onReplay();
            }}
            className={[
              "rounded-md border px-2 py-1 text-[10px] font-medium transition-colors",
              step.status === "failed"
                ? "border-rose-300 text-rose-700 hover:bg-rose-50"
                : "border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-slate-700",
            ].join(" ")}
            data-testid={`run-step-${step.step_order}-replay-button`}
            aria-label={`Replay step ${step.step_order} in isolation`}
          >
            Replay
          </button>
        )}
      </div>
      {expanded && (
        <div className="space-y-2 border-t border-slate-100 px-3 py-2 text-xs text-slate-600">
          {step.selector_used && (
            <div>
              Selector ({step.selector_strategy}):{" "}
              <code className="font-mono">{step.selector_used}</code>
            </div>
          )}
          {step.resolved_value && (
            <div>
              Value:{" "}
              <code className="font-mono">{step.resolved_value}</code>
            </div>
          )}
          {step.error_message && (
            <div className="rounded border border-rose-200 bg-rose-50 p-2 font-mono text-[11px] text-rose-700">
              {step.error_message}
            </div>
          )}

          {/* Phase REPLAY-FORENSICS — selector attempt log per step. */}
          {Array.isArray(step.selector_resolution) && step.selector_resolution.length > 0 && (
            <details
              className="rounded border border-slate-200 bg-slate-50 p-2"
              data-testid={`selector-attempts-${step.step_id}`}
            >
              <summary className="cursor-pointer select-none text-[11px] font-semibold text-slate-700">
                Selector attempts ({step.selector_resolution.length})
              </summary>
              <ul className="mt-2 space-y-1 text-[11px] font-mono">
                {step.selector_resolution.map((row, i) => (
                  <li
                    key={i}
                    className="flex items-baseline gap-2"
                    data-testid={`selector-attempt-row-${i}`}
                  >
                    <span
                      className={
                        "inline-block h-2 w-2 shrink-0 rounded-full " +
                        (row.hit ? "bg-emerald-500" : "bg-rose-500")
                      }
                      aria-label={row.hit ? "hit" : "miss"}
                    />
                    <span className="text-slate-500">{row.attempted_kind}</span>
                    <code className="truncate text-slate-700">{row.attempted_selector}</code>
                    <span className="ml-auto text-slate-400">{row.ms}ms</span>
                  </li>
                ))}
              </ul>
            </details>
          )}

          {/* Phase REPLAY-FORENSICS — "Why did this fail?" forensic diagnostic. */}
          {step.replay_failure_diagnostic && (
            <details
              className="rounded border border-amber-300 bg-amber-50 p-2"
              data-testid={`replay-failure-diagnostic-${step.step_id}`}
              open
            >
              <summary className="cursor-pointer select-none text-[11px] font-semibold text-amber-800">
                Why did this fail?
              </summary>
              <dl className="mt-2 grid grid-cols-[auto,1fr] gap-x-3 gap-y-1 text-[11px] text-slate-700">
                <dt className="text-slate-500">reason</dt>
                <dd className="font-mono">{step.replay_failure_diagnostic.reason}</dd>
                <dt className="text-slate-500">missing</dt>
                <dd className="font-mono">
                  {step.replay_failure_diagnostic.missing_selector_kind}/
                  {step.replay_failure_diagnostic.missing_selector_value}
                </dd>
                <dt className="text-slate-500">page url</dt>
                <dd className="truncate font-mono">
                  {step.replay_failure_diagnostic.page_url_at_failure || "—"}
                </dd>
                <dt className="text-slate-500">page title</dt>
                <dd className="truncate">
                  {step.replay_failure_diagnostic.page_title_at_failure || "—"}
                </dd>
                <dt className="text-slate-500">fallbacks tried</dt>
                <dd>{(step.replay_failure_diagnostic.fallbacks_tried || []).length}</dd>
              </dl>
              {step.replay_failure_diagnostic.failure_screenshot_path && (
                <p className="mt-2 text-[11px] text-amber-800">
                  Failure screenshot:{" "}
                  <code className="font-mono">
                    {step.replay_failure_diagnostic.failure_screenshot_path}
                  </code>
                </p>
              )}
            </details>
          )}
          {stepShot && (
            <AuthedImg
              runId={runId}
              assetId={stepShot.asset_id}
              alt={stepShot.filename}
              className="mt-1 max-h-48 rounded border border-slate-200"
            />
          )}
        </div>
      )}
    </li>
  );
}

// Fetches an asset as a blob and renders it as a Data URL — the artefact
// route requires auth so we can't just <img src=...> with the Bearer.
function AuthedImg({ runId, assetId, src, alt, className }) {
  const [objectUrl, setObjectUrl] = useState(null);
  useEffect(() => {
    let revoke = null;
    let cancelled = false;
    (async () => {
      try {
        const url = src ?? `/demo-runs/${runId}/artifacts/${assetId}`;
        const res = await api.get(url, { responseType: "blob" });
        if (cancelled) return;
        const u = URL.createObjectURL(res.data);
        revoke = u;
        setObjectUrl(u);
      } catch (e) {
        // swallow — UI will just show nothing
      }
    })();
    return () => {
      cancelled = true;
      if (revoke) URL.revokeObjectURL(revoke);
    };
  }, [runId, assetId, src]);
  if (!objectUrl) {
    return <div className={`${className || ""} bg-slate-100`} />;
  }
  return <img src={objectUrl} alt={alt} className={className} />;
}

function AuthedVideo({ runId, assetId }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let revoke = null;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(
          `/demo-runs/${runId}/artifacts/${assetId}`,
          { responseType: "blob" },
        );
        if (cancelled) return;
        const u = URL.createObjectURL(res.data);
        revoke = u;
        setSrc(u);
      } catch (e) {
        // swallow
      }
    })();
    return () => {
      cancelled = true;
      if (revoke) URL.revokeObjectURL(revoke);
    };
  }, [runId, assetId]);
  if (!src) return <Skeleton className="aspect-video w-full" />;
  return (
    <video
      controls
      preload="metadata"
      src={src}
      className="w-full rounded-lg border border-slate-200"
      data-testid="run-video"
    />
  );
}

function AuthedDownload({ runId, assetId, filename, label, testId }) {
  async function handle() {
    try {
      const res = await api.get(
        `/demo-runs/${runId}/artifacts/${assetId}`,
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }
  return (
    <button
      type="button"
      onClick={handle}
      className="inline-flex items-center gap-1.5 text-sm font-medium text-indigo-600 hover:text-indigo-700"
      data-testid={testId}
    >
      <Download size={13} />
      {label}
    </button>
  );
}
